import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { LogoutConfirmModal } from '@/components/buyer/LogoutConfirmModal';
import { ContactModal } from '@/components/common/ContactModal';
import { useToast } from '@/components/common/Toast';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { supabase } from '@/lib/supabase';

interface SellerData {
  name: string;
  email: string;
  phone: string;
  created_at: string;
}

interface RestaurantData {
  name: string;
  address: string;
  phone: string;
  description: string;
}

export function DesktopSellerMyPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { showToast, ToastContainer } = useToast();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [sellerData, setSellerData] = useState<SellerData | null>(null);
  const [restaurantData, setRestaurantData] = useState<RestaurantData | null>(null);

  // 구매자인 경우 구매자 마이페이지로 리다이렉트
  useEffect(() => {
    if (user && user.role === 'buyer') {
      navigate('/mypage', { replace: true });
      return;
    }
  }, [user, navigate]);

  // 판매자 및 레스토랑 데이터 가져오기 (판매자만)
  useEffect(() => {
    const fetchSellerData = async () => {
      if (!user?.id || user.role !== 'seller') return;

      try {
        // sellers 테이블에서 데이터 가져오기
        const { data: sellerInfo, error: sellerError } = await supabase
          .from('sellers')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (sellerError) {
          console.error('Failed to fetch seller data:', sellerError);
          return;
        }

        setSellerData(sellerInfo);

        // restaurants 테이블에서 데이터 가져오기
        const { data: restaurantInfo, error: restaurantError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('owner_id', user.id)
          .single();

        if (restaurantError) {
          console.error('Failed to fetch restaurant data:', restaurantError);
          return;
        }

        setRestaurantData(restaurantInfo);
      } catch (error) {
        console.error('Error fetching seller data:', error);
      }
    };

    fetchSellerData();
  }, [user]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleStoreInfoManagement = () => {
    navigate('/seller/store-edit');
  };

  const handleEvents = () => {
    navigate('/seller/mypage/events');
  };

  const handleNotices = () => {
    navigate('/seller/mypage/notices');
  };

  const handleTermsPolicies = () => {
    navigate('/seller/mypage/terms-policies');
  };

  const handleServiceInfo = () => {
    navigate('/seller/mypage/service-info');
  };

  const handleSettings = () => {
    navigate('/seller/settings');
  };

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut();
      setIsLogoutModalOpen(false);
      showToast('로그아웃되었습니다');
      // replace: true를 사용하여 히스토리를 교체하고 구매자 홈으로 이동
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      showToast('로그아웃에 실패했습니다');
    }
  };

  const handleLogoutCancel = () => {
    setIsLogoutModalOpen(false);
  };

  // 구매자인 경우 리다이렉트 중 표시할 로딩 화면
  if (user && user.role === 'buyer') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
          <p className="text-[#666666] text-base">구매자 마이페이지로 이동 중...</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute requiredRole="seller" minimalLoading={true}>
      <DesktopSellerPageContainer>
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-[32px] font-bold text-gray-800 text-center">마이페이지</h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - User Information */}
            <div className="lg:col-span-1">
              {/* Seller Profile Card */}
              {sellerData && restaurantData && (
                <div className="bg-white rounded-xl p-8 shadow-sm border border-[#86DB89]">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-[#86DB89] rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
                      </svg>
                    </div>
                    <h2 className="text-[24px] font-bold text-gray-800 mb-2">{restaurantData.name}</h2>
                    <p className="text-[16px] text-gray-600">판매자</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[14px] font-medium text-gray-500 mb-1">대표자명</label>
                      <p className="text-[16px] text-gray-800">{sellerData.name}</p>
                    </div>
                    <div>
                      <label className="block text-[14px] font-medium text-gray-500 mb-1">이메일</label>
                      <p className="text-[16px] text-gray-800">{sellerData.email}</p>
                    </div>
                    <div>
                      <label className="block text-[14px] font-medium text-gray-500 mb-1">전화번호</label>
                      <p className="text-[16px] text-gray-800">{sellerData.phone}</p>
                    </div>
                    <div>
                      <label className="block text-[14px] font-medium text-gray-500 mb-1">가입일</label>
                      <p className="text-[16px] text-gray-800">{formatDate(sellerData.created_at)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-[18px] font-semibold text-gray-800 mb-4">빠른 실행</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/seller/store')}
                    className="w-full text-left p-3 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                  >
                    내 가게 관리
                  </button>
                  <button
                    onClick={handleSettings}
                    className="w-full text-left p-3 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    설정
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Menu Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                <h3 className="text-[24px] font-semibold text-gray-800 mb-6">메뉴</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Account Management */}
                  <div className="space-y-2">
                    <h4 className="text-[18px] font-medium text-gray-700 mb-3">계정 관리</h4>
                    <button
                      onClick={handleSettings}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-[16px] text-gray-800">설정</span>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleStoreInfoManagement}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
                        </svg>
                        <span className="text-[16px] text-gray-800">가게 정보 관리</span>
                      </div>
                    </button>

                    <div className="w-full text-left p-4 rounded-lg border border-gray-200 bg-gray-50 opacity-50">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-[16px] text-gray-500">정산 (준비중)</span>
                      </div>
                    </div>

                    <div className="w-full text-left p-4 rounded-lg border border-gray-200 bg-gray-50 opacity-50">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                        <span className="text-[16px] text-gray-500">쿠폰 (준비중)</span>
                      </div>
                    </div>
                  </div>

                  {/* Information & Support */}
                  <div className="space-y-2">
                    <h4 className="text-[18px] font-medium text-gray-700 mb-3">정보 및 지원</h4>
                    <button
                      onClick={handleEvents}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0A1.99 1.99 0 013 15.546V6.456c0-.521.43-.955.95-.955C4.72 5.5 5.71 5.956 7 6.554" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l10 10-6-3z" />
                        </svg>
                        <span className="text-[16px] text-gray-800">이벤트</span>
                      </div>
                    </button>

                    <button
                      onClick={handleNotices}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                        <span className="text-[16px] text-gray-800">공지사항</span>
                      </div>
                    </button>

                    <button
                      onClick={() => setShowContactModal(true)}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span className="text-[16px] text-gray-800">문의하기</span>
                      </div>
                    </button>

                    <button
                      onClick={handleTermsPolicies}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-[16px] text-gray-800">약관 및 정책</span>
                      </div>
                    </button>

                    <button
                      onClick={handleServiceInfo}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[16px] text-gray-800">서비스 정보</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Logout Button */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleLogoutClick}
                    className="w-full p-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <div className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span className="text-[16px] font-medium">로그아웃</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 로그아웃 확인 모달 */}
        <LogoutConfirmModal
          isOpen={isLogoutModalOpen}
          onClose={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />

        <ContactModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
        />

        <ToastContainer />
      </DesktopSellerPageContainer>
    </ProtectedRoute>
  );
}